import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[var(--background)]">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <h1 className="text-3xl font-bold text-foreground mb-2">
          Terms of Service
        </h1>
        <p className="text-muted-foreground mb-8">
          Last updated: February 10, 2026
        </p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Parties</h2>
            <p>
              These Terms of Service ("Terms") govern your use of the Swiss Tax
              Platform operated by habitus.net GmbH ("Provider"), registered in
              Zurich, Switzerland. By using the service, you ("Customer") agree
              to these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              2. Service Description
            </h2>
            <p>
              The Swiss Tax Platform provides corporate tax management tools for
              businesses in Kanton Zurich, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Document-first company onboarding with AI-powered entity
                extraction
              </li>
              <li>Bank statement parsing and transaction categorization</li>
              <li>Swiss account plan (Kontenplan) management</li>
              <li>Tax report generation (Swiss GAAP FER compliant)</li>
              <li>Vendor and customer management</li>
              <li>Integration with Swiss Commercial Registry (Zefix)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              3. Subscription Plans
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Free:</strong> 1 company, 100 transactions/month, 10 AI
                credits.
              </li>
              <li>
                <strong>Starter (CHF 29/month):</strong> 3 companies, 1,000
                transactions/month, 100 AI credits.
              </li>
              <li>
                <strong>Professional (CHF 79/month):</strong> 10 companies,
                10,000 transactions/month, 500 AI credits.
              </li>
              <li>
                <strong>Enterprise (CHF 199/month):</strong> Unlimited companies
                and transactions, unlimited AI credits.
              </li>
            </ul>
            <p>
              Yearly plans receive a discount equivalent to 2 months free. All
              prices are in Swiss Francs (CHF) and exclude VAT where applicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              4. Payment and Billing
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Payments are processed by Stripe Inc. You agree to Stripe's
                terms of service.
              </li>
              <li>
                Subscriptions renew automatically at the end of each billing
                period.
              </li>
              <li>
                You may cancel at any time; access continues until the end of
                the current billing period.
              </li>
              <li>
                AI credit packs are one-time purchases and are non-refundable.
              </li>
              <li>
                Refunds for subscriptions are handled on a case-by-case basis
                within 14 days of purchase (Swiss consumer protection).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              5. Your Responsibilities
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                You are responsible for the accuracy and legality of data you
                upload.
              </li>
              <li>
                You must maintain the security of your account credentials.
              </li>
              <li>
                You may not use the service for money laundering, tax evasion,
                or other illegal activities.
              </li>
              <li>
                AI-generated outputs (categorizations, extractions) are
                suggestions. You are responsible for reviewing and approving
                them before use in official filings.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              6. Data Residency and Security
            </h2>
            <p>
              All data is hosted in Switzerland (Zurich) and the EU (Frankfurt).
              We comply with the Swiss Federal Act on Data Protection (nDSG) and
              GDPR. See our{" "}
              <Link href="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>{" "}
              for details.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              7. Limitation of Liability
            </h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                The service is provided "as is". We do not guarantee tax advice
                accuracy.
              </li>
              <li>
                The service does not replace professional tax advisory or
                auditing.
              </li>
              <li>
                Our liability is limited to the amount you paid in the 12 months
                preceding the claim.
              </li>
              <li>
                We are not liable for indirect, consequential, or punitive
                damages.
              </li>
              <li>
                We are not liable for losses resulting from reliance on
                AI-generated outputs without professional review.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              8. Intellectual Property
            </h2>
            <p>
              You retain all rights to your data. We retain rights to the
              platform software. By uploading data, you grant us a limited
              license to process it for service delivery. We do not use your
              financial data to train AI models.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Termination</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                You may terminate your account at any time via account settings.
              </li>
              <li>
                We may terminate accounts that violate these Terms with 30 days
                notice.
              </li>
              <li>
                Upon termination, your data will be available for export for 30
                days, then deleted (subject to legal retention requirements).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">
              10. Governing Law and Jurisdiction
            </h2>
            <p>
              These Terms are governed by Swiss law. Any disputes shall be
              resolved by the competent courts of Zurich, Switzerland. The UN
              Convention on Contracts for the International Sale of Goods (CISG)
              is excluded.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Changes</h2>
            <p>
              We may update these Terms from time to time. Material changes will
              be communicated via email at least 30 days before taking effect.
              Continued use after the effective date constitutes acceptance of
              the updated Terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
